import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import BluetoothConnectedIcon from '@mui/icons-material/BluetoothConnected';
import BluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled';
import RefreshIcon from '@mui/icons-material/Refresh';
import { usePrinter } from '../../../contexts/printer-context';

interface PrinterSelectorProps {
  compact?: boolean;
}

export const PrinterSelector: React.FC<PrinterSelectorProps> = ({ compact = false }) => {
  const {
    selectedPrinter,
    isConnecting,
    isConnected,
    scanForNewPrinter,
    connectToSelectedPrinter,
    isSupported,
  } = usePrinter();

  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const handleScan = async () => {
    setIsScanning(true);
    setScanError(null);
    
    try {
      // Use filtered mode to show only supported printers
      await scanForNewPrinter(false);
      setScanError(null);
    } catch (error) {
      console.error('[PrinterSelector] Scan error:', error);
      const errorMsg = (error as Error)?.message || 'Unknown error';
      
      if (errorMsg.includes('cancelled') || errorMsg.includes('cancel')) {
        setScanError(null);
      } else {
        setScanError(errorMsg);
      }
    } finally {
      setIsScanning(false);
    }
  };

  const handleReconnect = async () => {
    try {
      await connectToSelectedPrinter();
    } catch (error) {
      console.error('Error reconnecting to printer:', error);
    }
  };

  if (!isSupported) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Thermal printing requires Chrome or Edge browser.
        </Typography>
      </Alert>
    );
  }

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {selectedPrinter ? (
          <>
            <Chip
              icon={isConnected ? <BluetoothConnectedIcon /> : <BluetoothIcon />}
              label={selectedPrinter.name}
              size="small"
              sx={{
                bgcolor: isConnected ? 'success.light' : 'grey.200',
                color: isConnected ? 'success.dark' : 'text.secondary',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: isConnected ? 'success.dark' : 'text.secondary',
                }
              }}
            />
            {!isConnected && !isConnecting && (
              <Tooltip title="Reconnect">
                <Button 
                  size="small" 
                  onClick={handleReconnect} 
                  disabled={isConnecting}
                  variant="outlined"
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <RefreshIcon fontSize="small" />
                </Button>
              </Tooltip>
            )}
            {isConnecting && <CircularProgress size={16} />}
          </>
        ) : (
          <Button
            size="small"
            startIcon={isScanning ? <CircularProgress size={16} color="inherit" /> : <BluetoothIcon />}
            onClick={() => void handleScan()}
            disabled={isScanning}
            variant="contained"
            color="primary"
          >
            {isScanning ? 'Waiting...' : 'Pair Printer'}
          </Button>
        )}

        {scanError && (
          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
            {scanError}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Thermal Printer
      </Typography>

      {scanError && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          onClose={() => setScanError(null)}
        >
          <Typography variant="body2">
            {scanError}
          </Typography>
        </Alert>
      )}

      {!selectedPrinter && !scanError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Turn on your printer and click &quot;Pair Printer&quot; to connect.
          </Typography>
        </Alert>
      )}

      {selectedPrinter ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            p: 2,
            border: 1,
            borderColor: isConnected ? 'success.main' : 'grey.300',
            borderRadius: 2,
            backgroundColor: isConnected ? 'success.50' : 'grey.50',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 auto' }}>
            {isConnected ? (
              <BluetoothConnectedIcon sx={{ color: 'success.main', fontSize: 28 }} />
            ) : (
              <BluetoothDisabledIcon sx={{ color: 'grey.400', fontSize: 28 }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {selectedPrinter.name}
              </Typography>
              <Typography variant="caption" sx={{ color: isConnected ? 'success.main' : 'text.secondary' }}>
                {isConnected ? '● Connected' : '○ Disconnected'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!isConnected && (
              <Button
                size="small"
                onClick={handleReconnect}
                disabled={isConnecting}
                startIcon={isConnecting ? <CircularProgress size={16} /> : <RefreshIcon />}
                variant="contained"
                color="primary"
              >
                {isConnecting ? 'Connecting...' : 'Reconnect'}
              </Button>
            )}
            <Button 
              size="small" 
              onClick={() => void handleScan()} 
              disabled={isScanning} 
              variant="outlined"
            >
              Change Printer
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={isScanning ? <CircularProgress size={20} color="inherit" /> : <BluetoothIcon />}
          onClick={() => void handleScan()}
          disabled={isScanning}
          sx={{ py: 1.5 }}
        >
          {isScanning ? 'Waiting for selection...' : 'Pair Printer'}
        </Button>
      )}
    </Box>
  );
};
